import { ButtonBase, Collapse } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, Stack, styled, Text } from 'shell/components';
import { QuizRubricType } from 'utils/quizRubric';

const QuizQuestionCard = ({ question, criteria, rubricType, timelyGraded }) => {
    const { t } = useTranslation('quiz');
    const [expanded, setExpanded] = useState(false);
    const detailsId = 'quiz-question-details-' + question.id;
    const selectedCriteria = (question.criterion_ids || [])
        .map((criterionId) => criteria.find(({ id }) => id === criterionId))
        .filter(Boolean);
    return (
        <QuestionCard component={'article'} spacing={5}>
            <Stack direction={'row'} alignItems={'flex-start'} justifyContent={'space-between'} spacing={4}>
                <QuestionTitle component={'div'} variant={'p1'} weight={'medium'} rich>
                    {question.title || t('create.question-missing-content', 'Question text is unavailable.')}
                </QuestionTitle>
                <PointsTag $timelyGraded={timelyGraded} weight={'medium'}>
                    {t('create.points', 'Points: {{points}}', { points: question.points_possible ?? 0 })}
                </PointsTag>
            </Stack>
            {timelyGraded && (
                <>
                    <DetailsButton
                        aria-controls={detailsId}
                        aria-expanded={expanded}
                        onClick={() => setExpanded((current) => !current)}
                    >
                        <Text weight={'medium'}>{t('view.questions.details', 'Details')}</Text>
                        <Icon kind={expanded ? 'chevronUp' : 'chevronDown'} sx={{ fontSize: 16 }} />
                    </DetailsButton>
                    <Collapse in={expanded} timeout={200} unmountOnExit>
                        <QuestionDetails id={detailsId} spacing={5}>
                            {rubricType === QuizRubricType.QUESTION && (
                                <Stack spacing={2}>
                                    <Text weight={'medium'}>
                                        {t('view.questions.criteria', 'Criterion/Criteria for this question:')}
                                    </Text>
                                    {selectedCriteria.length ? (
                                        selectedCriteria.map((criterion) => (
                                            <Text key={criterion.id} color={'light'}>
                                                {criterion.title}
                                            </Text>
                                        ))
                                    ) : (
                                        <Text color={'light'}>{t('common:ui.none', 'None')}</Text>
                                    )}
                                </Stack>
                            )}
                            <Stack spacing={2}>
                                <Text weight={'medium'}>{t('view.questions.grading-notes', 'Grading Notes:')}</Text>
                                {question.grading_notes ? (
                                    <Text color={'light'} rich>
                                        {question.grading_notes}
                                    </Text>
                                ) : (
                                    <Text color={'light'}>{t('common:ui.none', 'None')}</Text>
                                )}
                            </Stack>
                        </QuestionDetails>
                    </Collapse>
                </>
            )}
        </QuestionCard>
    );
};

const QuestionCard = styled(Stack)(({ theme }) => ({
    width: '100%',
    overflow: 'hidden',
    padding: theme.spacing(5),
    border: `1px solid ${theme.palette.ensightful.neutral[300]}`,
    borderRadius: '16px',
    background: theme.palette.ensightful.shades[0],
}));

const QuestionTitle = styled(Text)(() => ({
    minWidth: 0,
    '& img, & video, & iframe': { maxWidth: '100%' },
    '& table': { display: 'block', maxWidth: '100%', overflowX: 'auto' },
}));

const PointsTag = styled(Text, {
    shouldForwardProp: (prop) => prop !== '$timelyGraded',
})(({ $timelyGraded, theme }) => ({
    flexShrink: 0,
    padding: theme.spacing(1, 2),
    borderRadius: '40px',
    whiteSpace: 'nowrap',
    color: $timelyGraded ? theme.palette.ensightful.primary[700] : theme.palette.ensightful.neutral[900],
    background: $timelyGraded ? theme.palette.ensightful.primary[50] : theme.palette.ensightful.neutral[100],
}));

const DetailsButton = styled(ButtonBase)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    margin: theme.spacing(-1),
    borderRadius: '4px',
    color: theme.palette.ensightful.neutral[900],
    '&:focus-visible': {
        outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
        outlineOffset: '2px',
    },
}));

const QuestionDetails = styled(Stack)(() => ({
    width: '100%',
}));

export default QuizQuestionCard;
